import { createContext, type ComponentChildren } from 'preact';
import { useContext, useEffect } from 'preact/hooks';
import { useState } from 'react';
import { trackVariants } from '../services/TrackingService.ts';

type Variants = Set<string>;

const EVENT_NAME = 'CATALOG_AB_TEST_READY';
const VARIANTS_KEY = 'CATALOG_AB_TEST_VARIANTS';
const ABTestContext = createContext<Variants>(new Set());

export type ABTestProviderProps = {
  children: ComponentChildren;
};

/**
 * NOTE: window.CATALOG_AB_TEST_VARIANTS is a set of variants,
 * ex: new Set(['quick-view-variant', 'add-to-cart-variant']);
 *
 * @docs https://nestle.atlassian.net/wiki/spaces/DPTO/pages/450543553/A+B+Testing+Integration+Optimizely+Adobe+Target
 */
export function ABTestProvider({ children }: ABTestProviderProps) {
  const [variants, setVariants] = useState<Variants>(
    new Set(
      import.meta.env.SSR ? undefined : (window[VARIANTS_KEY] as Variants)
    )
  );

  function handleVariants() {
    const newVariantsArray = window[VARIANTS_KEY] as Variants;
    const isNew = newVariantsArray && newVariantsArray !== variants;
    const newVariants = new Set(window[VARIANTS_KEY] as Variants);

    if (isNew) {
      setVariants(newVariants);
    }

    trackVariants(newVariants);
  }

  useEffect(() => {
    if (window[VARIANTS_KEY]) {
      handleVariants();
    }

    window.addEventListener(EVENT_NAME, handleVariants);
    return () => window.removeEventListener(EVENT_NAME, handleVariants);
  }, []);

  if (variants?.size && import.meta.env.VITEST !== 'true') {
    // eslint-disable-next-line no-console
    console.log('Running A/B test variants:', Array.from(variants));
  }

  return (
    <ABTestContext.Provider value={variants}>{children}</ABTestContext.Provider>
  );
}

/**
 *  Hook to get the current A/B test variant.
 *
 * @example
 *
 * ```tsx
 * const isFoo = useABTestVariant('foo');
 *
 * return isFoo ? <div>foo</div> : <div>bar</div>;
 * ```
 */
export default function useABTestVariant(variant: string | string[]): boolean {
  const variants = useContext(ABTestContext);

  if (Array.isArray(variant)) {
    return variant.some((v) => variants.has(v));
  }

  return variants.has(variant);
}
